// Email + password login (linked from /login SSO landing)
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { useTheme } from '../contexts/ThemeContext';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { APP_NAME } from '../helpers/constants';
import { USER_ROLES } from '../helpers/constants';
import { loginUser } from '../services/authService';
import { encryptPassword } from '@/utils/crypto';
import { getPostLoginRedirect, clearPostLoginRedirect } from '../utils/auth';
import { isAdminUIDisabled } from '../config/runtimeConfig';

const EmailLogin: React.FC = () => {
  const muiTheme = useMuiTheme();
  const { isDarkMode } = useTheme();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { loginWithResponse, isAuthenticated, isAdmin, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [redirectPath] = useState(() => getPostLoginRedirect());

  const pendingMessage = location.state?.message;

  useEffect(() => {
    if (isAuthenticated) {
      // Platform is admin-only now — everyone else works from Gmail/Outlook/Google Chat directly.
      if (!isAdminUIDisabled() && !isAdmin) {
        clearPostLoginRedirect();
        navigate('/adminOnly', { replace: true });
        return;
      }
      if (redirectPath) {
        clearPostLoginRedirect();
        navigate(redirectPath, { replace: true });
      } else {
        navigate('/supportSpace', { replace: true });
      }
    }
  }, [isAuthenticated, isAdmin, navigate, user, redirectPath]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (!username || !password) {
        setError('Please fill in all fields');
        return;
      }

      const encryptedPassword = await encryptPassword(password);

      const response = await loginUser({
        email_id: username,
        password: encryptedPassword,
        encrypted: true,
      });

      loginWithResponse(response);
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Login failed. Please check your credentials.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        p: 2,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        <Box
          component="img"
          src={isDarkMode ? '/fulllogo-white.png' : '/fulllogo.png'}
          alt={APP_NAME}
          sx={{
            height: 48,
            width: 'auto',
            maxWidth: '100%',
            objectFit: 'contain',
          }}
        />
      </Box>

      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 400,
          width: '100%',
          borderRadius: 2,
        }}
      >
        <Typography variant="h5" component="h1" align="center" sx={{ fontWeight: 'bold', mb: 0.5 }}>
          Welcome Back
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
          Sign in to continue to your account
        </Typography>

        {pendingMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {pendingMessage}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="Email"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            margin="normal"
            required
            disabled={loading}
            type="email"
          />

          <TextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            required
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    disabled={loading}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 0 }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Sign In'}
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
          <Link
            to="/login"
            style={{
              color: muiTheme.palette.primary.main,
              textDecoration: 'none',
              fontWeight: 500,
            }}
          >
            Back to sign-in options
          </Link>
        </Typography>

        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
          <Link
            to="/forgot-password"
            style={{
              color: muiTheme.palette.primary.main,
              textDecoration: 'none',
              fontWeight: 500,
            }}
          >
            Forgot your password?
          </Link>
        </Typography>

        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
          Don&apos;t have an account?{' '}
          <Link
            to="/companySignup"
            style={{
              color: muiTheme.palette.primary.main,
              textDecoration: 'none',
              fontWeight: 500,
            }}
          >
            Sign up here
          </Link>
        </Typography>
      </Paper>

      <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 3 }}>
        © {new Date().getFullYear()} {APP_NAME}. All rights reserved.
      </Typography>
    </Box>
  );
};

export default EmailLogin;
